import * as React from "react";
import {PremiumType, UPFRONT_PAYMENT_THRESHOLD} from "@falcon/services";
import {getAmountToDisplay, hasUpfrontPaymentAmount} from "../../../../../../CommonFunctions";
import {MtaSuccessModalWording} from "../../../../common/modal/MtaSuccessModalWording";
import {IFactors} from "../../../../common/modal/GeneratorInputTypes";
import * as successModals from "../../../../../../components/modals/SuccessModalRenderers";

interface IPaymentDetails {
    existingCardPayment?: boolean,
    instalmentPlan: {
        monthlyPayment: number | null,
        totalAmount: number
    }
}

export class EditCarMtaSuccessModalWordingGenerator {

    public static readonly generateModalWording = (
        factors: IFactors,
        paymentDetails: IPaymentDetails,
        payFullPrice: boolean,
        mtaDetailsWording: string | null,
        cardNumber: string,
        agreeToReuseCardDetails: boolean | null,
        previousMonthlyPremium: any
    ): MtaSuccessModalWording => {
        return new MtaSuccessModalWording(
            EditCarMtaSuccessModalWordingGenerator.generateModalTitle(
                factors,
                payFullPrice,
                !this.areInstalmentsPayedOff(factors, paymentDetails)
            ),
            EditCarMtaSuccessModalWordingGenerator.generateModalBody(
                factors,
                paymentDetails,
                mtaDetailsWording,
                payFullPrice,
                cardNumber,
                agreeToReuseCardDetails,
                previousMonthlyPremium
            ),
            EditCarMtaSuccessModalWordingGenerator.generateAnythingElseChangedModalTitle(),
            EditCarMtaSuccessModalWordingGenerator.generateAnythingElseChangedModalBody()
        );
    }

    public static readonly generateModalTitle = (factors: IFactors, payFullPrice: boolean, instalmentsNotPaidOff: boolean): React.ReactNode => {
        const {additionalPremium, returnPremium} = factors;
        const premiumType: PremiumType = this.getPremiumType(factors.premiumType);

        if (additionalPremium > 0 || factors.mtaAndDebtCombinedAdditionalAmount > 0) {
            if (premiumType === PremiumType.annual || factors.mtaAndDebtCombinedAdditionalAmount > 0 || payFullPrice) {
                return "Payment was successful";
            } else if (premiumType === PremiumType.monthly) {
                return "Payment was successfully added to your payment plan";
            } else {
                return EditCarMtaSuccessModalWordingGenerator.defaultModalTitle;
            }

        } else if (returnPremium > 0) {

            switch (premiumType) {
                case PremiumType.annual:
                    return "Your refund is on the way";
                case PremiumType.monthly:
                    if (instalmentsNotPaidOff) {
                        return "Your payment plan has been updated";
                    } else {
                        return "Your refund is on the way";
                    }
                default:
                    return EditCarMtaSuccessModalWordingGenerator.defaultModalTitle;
            }

        } else {
            return EditCarMtaSuccessModalWordingGenerator.defaultModalTitle;
        }
    }

    public static readonly generateModalBody = (
        factors: IFactors,
        paymentDetails: IPaymentDetails,
        mtaDetailsWording: string | null,
        payFullPrice: boolean,
        cardNumber: string,
        agreeToReuseCardDetails: boolean | null,
        previousMonthlyPremium: any
    ): React.ReactNode => {
        const instalmentPlanMonthlyPremium: number | null = paymentDetails.instalmentPlan.monthlyPayment;
        const {
            additionalPremium,
            returnPremium
        } = factors;
        const premiumType: PremiumType = this.getPremiumType(factors.premiumType);

        if (additionalPremium > 0 || factors.mtaAndDebtCombinedAdditionalAmount > 0) {
            const didSwitchPaymentCard: boolean = !!agreeToReuseCardDetails && !paymentDetails.existingCardPayment;
            return EditCarMtaSuccessModalWordingGenerator.getAdditionalPremiumWording(
                factors,
                premiumType,
                mtaDetailsWording,
                payFullPrice,
                cardNumber,
                didSwitchPaymentCard,
                previousMonthlyPremium
            );
        } else if (returnPremium > 0 || factors.mtaAndDebtCombinedReturnAmount > 0) {
            return EditCarMtaSuccessModalWordingGenerator.getRefundWording(
                factors,
                premiumType,
                mtaDetailsWording,
                instalmentPlanMonthlyPremium,
                previousMonthlyPremium,
                !this.areInstalmentsPayedOff(factors, paymentDetails)
            );
        } else {
            return "There is no extra cost for the changes you have made.";
        }
    }

    private static readonly getAdditionalPremiumWording = (
        factors: IFactors,
        premiumType: PremiumType,
        mtaDetailsWording: string | null,
        payFullPrice: boolean,
        cardNumber: string,
        didSwitchPaymentCard: boolean | null,
        previousMonthlyPremium: any
    ): React.ReactNode => {
        const {
            additionalPremium,
            upfrontPaymentAmount,
            monthlyPaymentSpreadAmount,
            mtaAndDebtCombinedAdditionalAmount
        } = factors;

        const wordingToShow = mtaDetailsWording ?? "for the changes you have made";

        if (additionalPremium > 0 && (premiumType === PremiumType.annual || payFullPrice) && cardNumber) {
            const modalContent = (<>{wordingToShow}.<br/></>)
            return successModals.paymentSuccessfulAdditionalPremiumModal(factors.additionalPremium, cardNumber, modalContent, didSwitchPaymentCard);
        } else if ((additionalPremium > 0 || mtaAndDebtCombinedAdditionalAmount > 0) && premiumType === PremiumType.monthly && hasUpfrontPaymentAmount(factors) && !payFullPrice && cardNumber) {
            const modalContent = (<>{wordingToShow}.<br/></>)
            return successModals.paymentSuccessfulAdditionalPremiumMonthlyModal(
                upfrontPaymentAmount,
                monthlyPaymentSpreadAmount,
                cardNumber,
                modalContent,
                didSwitchPaymentCard
            );
        } else if (additionalPremium > 0 && premiumType === PremiumType.monthly && upfrontPaymentAmount < UPFRONT_PAYMENT_THRESHOLD && !payFullPrice) {
            return successModals.paymentPlanUpdatedAdditionalPremiumModal(factors, previousMonthlyPremium);
        }
        return "";
    }

    private static readonly getRefundWording = (
        factors: IFactors,
        premiumType: PremiumType,
        mtaDetailsWording: string | null,
        instalmentPlanMonthlyPremium: number | null,
        previousMonthlyPremium: any,
        instalmentsNotPaidOff: boolean
    ): React.ReactNode => {

        const {
            returnPremium,
            monthlyPremium
        } = factors;

        const wordingToShow = mtaDetailsWording ?? "for the changes you have made";

        if (returnPremium > 0 && premiumType === PremiumType.annual) {
            return (
                <p className="font-weight-extra-light">
                    {getAmountToDisplay(returnPremium)} has been returned {" "}
                    {wordingToShow}
                    .
                </p>
            )
        } else if (returnPremium > 0 && premiumType === PremiumType.monthly) {
            const monthlyPremiumToShow: number = instalmentPlanMonthlyPremium === null ? monthlyPremium : instalmentPlanMonthlyPremium;
            return successModals.paymentPlanUpdatedRefundModal(returnPremium, monthlyPremiumToShow, previousMonthlyPremium, instalmentsNotPaidOff);
        }
        return "";
    }

    public static readonly generateAnythingElseChangedModalTitle = (): React.ReactNode => {
        return EditCarMtaSuccessModalWordingGenerator.anythingElseChangedModalTitle;
    }

    public static readonly generateAnythingElseChangedModalBody = (): React.ReactNode => {
        return EditCarMtaSuccessModalWordingGenerator.anythingElseChangedModalBody;
    }

    private static readonly defaultModalTitle: string = "Change was successful";
    private static readonly anythingElseChangedModalTitle: string = "Has anything else changed?";
    private static readonly anythingElseChangedModalBody: string = "Please check everything’s still accurate and make any changes required so you can be sure all policy details are up to date.";

    private static areInstalmentsPayedOff(factors: IFactors, paymentDetails: IPaymentDetails): boolean {
        return factors.premiumType !== PremiumType.monthly || paymentDetails.instalmentPlan.totalAmount <= 0;
    }

    private static getPremiumType(premiumType: string): PremiumType {
        return PremiumType[premiumType as keyof typeof PremiumType];
    }

}
